import type { Spec } from '@json-render/core';
import { z } from 'zod';

/**
 * Analytics event configuration that can be attached to components.
 * When present, the renderer will fire this event on the corresponding action.
 */
export type AnalyticsEvent = {
  /** The event name to fire (e.g., "insights:section_expanded") */
  event: string;
  /** Optional static properties to include with the event */
  properties?: Record<string, unknown>;
};

export type ExpandableAnalytics = {
  onExpand?: AnalyticsEvent;
  onCollapse?: AnalyticsEvent;
};

export type LinkAnalytics = {
  onClick?: AnalyticsEvent;
};

export const analyticsEventSchema = z.object({
  event: z.string(),
  properties: z.record(z.unknown()).optional(),
});

export const expandableAnalyticsSchema = z.object({
  onExpand: analyticsEventSchema.optional(),
  onCollapse: analyticsEventSchema.optional(),
});

export const linkAnalyticsSchema = z.object({
  onClick: analyticsEventSchema.optional(),
});

/**
 * Zod schema for validating json-render Spec objects.
 *
 * Each element requires `type` (string) as a minimum structural invariant.
 * `.passthrough()` preserves additional fields (visible, on, watch, repeat)
 * that have complex recursive types impractical to express in Zod.
 *
 * The type is cast to align Zod's inferred output with the @json-render/core
 * Spec type, since UIElement doesn't carry an index signature.
 */
export const specSchema: z.ZodType<Spec> = z.object({
  root: z.string(),
  elements: z.record(z.object({ type: z.string() }).passthrough()),
  state: z.record(z.unknown()).optional(),
}) as unknown as z.ZodType<Spec>;
